import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../../v3";
import { ProjectService } from "../../../services";
import { getStageTableColumns } from "../../../pages/library/stages/constants";
import { AddEditStage } from "./AddEditStage.component";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../../utils";
import { DELETE_STAGE, EDIT_STAGE } from "../../../constants";
import { handlePdfDownload, Listing } from "../../pdf";

export const StageList = ({ totalCount = 0, setTotalCount = () => {}, onSuccess = () => {}, reloadData = 0 }) => {
  const [stages, setStages] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [stageId, setStageId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "updatedAt", sort: "desc" }]);
  const [paginationState, setPaginationState] = useState({
    page: 0,
    pageSize: 10,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    categoryId: null,
  });

  const { user } = useSelector((state) => state.user);

  const hasEditStage = useHasPermission(EDIT_STAGE);
  const hasDeleteStage = useHasPermission(DELETE_STAGE);

  const fetchData = useCallback(
    async (stageFilters = {}) => {
      setLoading(true);
      try {
        const { data } = await ProjectService.getStages({ ...stageFilters });
        setStages(data.rows);
        setTotalCount(data.count);
      } catch (error) {
        toast.error("Failed to fetch stages");
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={"Stages"} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Stages",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleOpenModal = (stageId) => {
    setStageId(stageId);
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = (stageData) => {
    setDeleteModal({
      isOpen: true,
      stageId: stageData.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, stageId: null });
  };

  const handleDelete = async () => {
    try {
      await ProjectService.deleteStage(deleteModal.stageId);
      setDeleteModal({ isOpen: false, stageId: null });
      toast.success("Stage deleted successfully");
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to delete stage");
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const handleActions = (stageData, actionType) => {
    switch (actionType) {
      case "edit":
        handleOpenModal(stageData?.id);
        break;
      case "delete":
        openDeleteModal(stageData);
        break;
      default:
        break;
    }
  };

  const columnHeaders = getStageTableColumns(handleActions, hasEditStage, hasDeleteStage);

  const rows = stages.map((stage, index) => ({
    sl: index + 1 + paginationState.page * paginationState.pageSize,
    id: stage.id,
    name: stage.name,
    type: stage.type,
    description: stage.description,
  }));
  return (
    <>
      <CardLayout>
        <MTable
          // Search data
          placeholder="Search stages"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export data
          showExportButton // TODO: implement permission
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
        />
      </CardLayout>

      {/* edit stage modal */}
      <AddEditStage open={openModal} stageId={stageId} onClose={handleCloseModal} onSuccess={onSuccess} />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this stage?"
      />
    </>
  );
};
